import React, { useRef, useState, DragEvent } from 'react';
import { apiUploadImage } from '../../api/api';

interface ImageUploaderProps {
  value: string[];
  onChange: (urls: string[]) => void;
  maxImages?: number;
}

export default function ImageUploader({ value, onChange, maxImages = 3 }: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<boolean[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const available = maxImages - value.length;
    const toUpload = Array.from(files).slice(0, available);
    if (toUpload.length === 0) return;

    const newUploading = toUpload.map(() => true);
    setUploading(newUploading);
    setErrors([]);

    const results = await Promise.allSettled(toUpload.map((f) => apiUploadImage(f)));

    const newUrls: string[] = [];
    const newErrors: string[] = [];
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        newUrls.push(result.value);
      } else {
        newErrors.push(result.reason instanceof Error ? result.reason.message : 'Upload failed');
      }
    });

    onChange([...value, ...newUrls]);
    setUploading([]);
    setErrors(newErrors);
  };

  const handleRemove = (index: number) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const canUploadMore = value.length < maxImages && uploading.length === 0;

  return (
    <div>
      {/* Preview grid */}
      {value.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0.5rem',
            marginBottom: '0.75rem',
          }}
        >
          {value.map((url, i) => (
            <div
              key={i}
              style={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #1a2744',
              }}
            >
              <img
                src={url}
                alt={`Upload ${i + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <button
                type="button"
                onClick={() => handleRemove(i)}
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  color: '#fff',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          ))}

          {/* Uploading spinners */}
          {uploading.map((_, i) => (
            <div
              key={`uploading-${i}`}
              style={{
                aspectRatio: '1',
                borderRadius: '8px',
                backgroundColor: '#1a2744',
                border: '1px solid #1a2744',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid #1a2744',
                  borderTop: '2px solid #FF9900',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Upload area */}
      {canUploadMore && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragging ? '#FF9900' : '#1a2744'}`,
            borderRadius: '10px',
            padding: '2rem 1rem',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragging ? 'rgba(255,153,0,0.05)' : 'transparent',
            transition: 'all 0.2s',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📷</div>
          <p style={{ color: '#c8d0e0', fontSize: '0.875rem', margin: '0 0 0.25rem' }}>
            Click to upload or drag & drop
          </p>
          <p style={{ color: '#6b7a99', fontSize: '0.75rem', margin: 0 }}>
            Up to {maxImages} images • {maxImages - value.length} remaining
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          {errors.map((err, i) => (
            <p
              key={i}
              style={{
                color: '#ef4444',
                fontSize: '0.8rem',
                margin: '0.25rem 0',
                padding: '0.4rem 0.75rem',
                backgroundColor: 'rgba(239,68,68,0.1)',
                borderRadius: '6px',
              }}
            >
              {err}
            </p>
          ))}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
